import type { WorldObject } from '@/types'
import { proxyImageUrl, withProxiedImages } from '@/services/media/proxyUrl'

let ALL_OBJECTS: WorldObject[] = []
let BY_ID = new Map<string, WorldObject>()
let loadPromise: Promise<void> | null = null
const detailsLoadPromises = new Map<string, Promise<void>>()
let allDetailsPromise: Promise<void> | null = null
const loadedDetails = new Set<string>()
const galleryLoadPromises = new Map<string, Promise<string[]>>()

/** Load the search/globe summaries once; facts are fetched on demand below. */
export function ensureLandmarksLoaded(): Promise<void> {
  if (!loadPromise) {
    loadPromise = fetch('/landmarks-index.json')
      .then((res) => { if (!res.ok) throw new Error('Unable to load places'); return res.json() as Promise<WorldObject[]> })
      .then((raw) => {
        ALL_OBJECTS = raw.map(withProxiedImages).sort((a, b) => a.name.localeCompare(b.name))
        BY_ID = new Map(ALL_OBJECTS.map((obj) => [obj.id, obj]))
      })
  }
  return loadPromise
}

function mergeDetails(detail: Pick<WorldObject, 'id' | 'sections' | 'funFacts' | 'didYouKnow' | 'translations'>) {
  const object = BY_ID.get(detail.id)
  if (object) { Object.assign(object, detail); loadedDetails.add(detail.id) }
}

/** Preserve object identity so progress, games and related-place references stay valid. */
export function ensureWorldObjectLoaded(id: string): Promise<void> {
  if (!detailsLoadPromises.has(id)) {
    const promise = ensureLandmarksLoaded().then(async () => {
      if (!BY_ID.has(id) || loadedDetails.has(id)) return
      const response = await fetch('/landmark-details/' + encodeURIComponent(id) + '.json')
      if (!response.ok) throw new Error('Unable to load place facts')
      mergeDetails(await response.json())
    })
    detailsLoadPromises.set(id, promise)
  }
  return detailsLoadPromises.get(id)!
}

/** Random fact games need the full dataset, never the homepage. */
export function ensureAllWorldObjectsLoaded(): Promise<void> {
  if (!allDetailsPromise) allDetailsPromise = ensureLandmarksLoaded().then(async () => {
    const response = await fetch('/landmarks-details.json')
    if (!response.ok) throw new Error('Unable to load game facts')
    const details = await response.json() as WorldObject[]
    details.forEach(mergeDetails)
  })
  return allDetailsPromise
}

/**
 * Fetches a single place's extra gallery photos (public/gallery/<id>.json,
 * absent entirely for places with none) and mutates that place's `gallery`
 * field in place on the shared object already sitting in BY_ID/ALL_OBJECTS
 * — so getWorldObjectById(id).gallery starts returning the real array right
 * after this resolves, with no separate merge step needed by callers.
 * Memoized per id; safe to call repeatedly (ExplorePage on every visit,
 * PhotoGallery's lightbox, etc.) without duplicate fetches. Deliberately
 * NOT part of the app-level Suspense gate above — ExplorePage/ModelDetailPage
 * call this themselves and re-render once it resolves, so a place's hero/
 * facts/map render immediately and its gallery strip just pops in a beat
 * later instead of blocking the whole page (see those pages' own comments).
 */
export function ensureGalleryLoaded(id: string): Promise<string[]> {
  let promise = galleryLoadPromises.get(id)
  if (!promise) {
    promise = fetch(`/gallery/${id}.json`)
      .then((res) => (res.ok ? (res.json() as Promise<string[]>) : []))
      .catch(() => [])
      .then((raw) => {
        const proxied = raw.map(proxyImageUrl)
        const obj = BY_ID.get(id)
        if (obj) obj.gallery = proxied
        return proxied
      })
    galleryLoadPromises.set(id, promise)
  }
  return promise
}

export function getAllWorldObjects(): WorldObject[] {
  return ALL_OBJECTS
}

export function getWorldObjectById(id: string): WorldObject | undefined {
  return BY_ID.get(id)
}

export function getRandomWorldObject(excludeId?: string): WorldObject {
  const pool = excludeId ? ALL_OBJECTS.filter((o) => o.id !== excludeId) : ALL_OBJECTS
  return pool[Math.floor(Math.random() * pool.length)]
}

export function getRelatedObjects(worldObject: WorldObject): WorldObject[] {
  return worldObject.relatedObjects
    .map((id) => BY_ID.get(id))
    .filter((o): o is WorldObject => Boolean(o))
}

export function getWorldObjectsWithModel3d(): (WorldObject & { model3d: NonNullable<WorldObject['model3d']> })[] {
  return ALL_OBJECTS.filter(
    (obj): obj is WorldObject & { model3d: NonNullable<WorldObject['model3d']> } => Boolean(obj.model3d),
  )
}

export function searchWorldObjects(query: string, limit = 8): WorldObject[] {
  const q = query.trim().toLowerCase()
  if (!q) return []

  return ALL_OBJECTS.map((obj) => ({ obj, score: matchScore(obj, q) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((r) => r.obj)
}

function matchScore(obj: WorldObject, q: string): number {
  const name = obj.name.toLowerCase()
  const country = obj.country.toLowerCase()

  if (name === q) return 100
  if (name.startsWith(q)) return 80
  if (name.includes(q)) return 60
  if (country.startsWith(q)) return 50
  if (country.includes(q)) return 40
  if (obj.tags.some((t) => t.toLowerCase() === q)) return 45
  if (obj.tags.some((t) => t.toLowerCase().includes(q))) return 30
  if (obj.type.includes(q)) return 20
  if (obj.continent.toLowerCase().includes(q)) return 15
  return 0
}
